import { action, computed, makeObservable, observable, when } from 'mobx';
import { ROUTES } from '../routes';
import { AccountData } from '../types';
import { SocialProviders } from '../utils/presets';
import { RootStore } from './root';
import { shouldHideFacebookUnlessConnected } from './constants';

export const UNSUPPORTED_AGENTS_FOR_SOCIAL_AUTH = [
  'FBAV',
  'FBAN',
  'Instagram',
  'musical_ly',
  'Twitter',
];
const CODUX_HOST = 'online.codux.com';

export class SocialAuthStore {
  protected readonly rootStore: RootStore;
  socialConnectionsProviders: string[] = [];
  // Social connections of the account resolved by `/userAccountsByEmail`, used
  // only by the Symphony Facebook gate in `showFacebook`. Deliberately kept
  // separate from `socialConnectionsProviders`: that one feeds `enableAccess`,
  // which guards the /socialOnly route, so it must stay owned by the
  // social-only flow.
  accountSocialConnectionsProviders: string[] = [];
  showGoogle: boolean;
  // Base Facebook visibility from the active preset + the legacy
  // `specs.ident.HideFacebookButton` kill-switch, captured at construction.
  // The public `showFacebook` computed layers the Symphony gate on top.
  private readonly showFacebookByPreset: boolean;
  showApple: boolean;
  showAlreadyExistsNote: boolean = false;
  showEditorXSignupDisabledModal: boolean = false;
  isUnsupportedAgentForSocialAuth: boolean = false;
  // Google-only in-app-browser gate (e.g. LinkedIn's webview): Google rejects
  // OAuth from embedded browsers, so clicking Google expands an inline notice
  // right below the button offering to reopen the page in the system browser.
  // Facebook/Apple work in those webviews and stay untouched.
  showGoogleInAppBrowserNotice: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const { preset } = this.rootStore.displayStore;
    const shouldHideFBButton = this.rootStore.experiments.enabled(
      'specs.ident.HideFacebookButton',
    );
    this.showGoogle = preset.socialProviders?.includes(SocialProviders.GOOGLE)!;
    this.showFacebookByPreset =
      !shouldHideFBButton &&
      preset.socialProviders?.includes(SocialProviders.FACEBOOK)!;
    this.showApple =
      preset.socialProviders?.includes(SocialProviders.APPLE)! &&
      location.host != CODUX_HOST;
    this.isUnsupportedAgentForSocialAuth =
      this.rootStore.experiments.enabled(
        'specs.ident.shouldBlockInAppBrowsersLogin',
      ) &&
      UNSUPPORTED_AGENTS_FOR_SOCIAL_AUTH.findIndex((ua) =>
        navigator?.userAgent?.includes(ua),
      ) !== -1;

    makeObservable(this, {
      socialConnectionsProviders: observable,
      accountSocialConnectionsProviders: observable,
      showGoogle: observable,
      showApple: observable,
      showAlreadyExistsNote: observable,
      showEditorXSignupDisabledModal: observable,
      isUnsupportedAgentForSocialAuth: observable,
      showGoogleInAppBrowserNotice: observable,
      setShowGoogleInAppBrowserNotice: action.bound,
      showFacebook: computed,
      shouldHideSocialAuth: computed,
      showIconsError: computed,
      enableAccess: computed,
      setSocialConnectionsProviders: action.bound,
      setAccountSocialConnectionsProviders: action.bound,
      setSocialProvidersAndNavigate: action.bound,
    });
  }

  setSocialConnectionsProviders(providers: string[]) {
    this.socialConnectionsProviders = providers;
  }

  setShowGoogleInAppBrowserNotice(show: boolean) {
    this.showGoogleInAppBrowserNotice = show;
  }

  setAccountSocialConnectionsProviders(providers: string[]) {
    this.accountSocialConnectionsProviders = providers;
  }

  /**
   * Whether to render the Facebook SSO button.
   *
   * Base visibility comes from the active preset (and the legacy
   * `specs.ident.HideFacebookButton` kill-switch) captured at construction as
   * `showFacebookByPreset`.
   *
   * Symphony gate: for the Symphony userType with the
   * `shouldSymphonyHideFacebookUnlessConnected` Conductor FT on, Facebook is
   * shown only when the account actually has a facebook social connection
   * (`"facebook"` in `accountSocialConnectionsProviders`). That list is empty
   * until the `/userAccountsByEmail` lookup resolves and is reset on every
   * lookup, so before the email step the button is hidden and it re-appears
   * only while the currently resolved account has a facebook connection.
   * Every other userType / when the FT is off, behaviour is unchanged.
   */
  get showFacebook(): boolean {
    if (!this.showFacebookByPreset) {
      return false;
    }
    const { displayStore, experiments } = this.rootStore;
    if (
      displayStore.isSymphony &&
      shouldHideFacebookUnlessConnected(experiments)
    ) {
      return this.accountSocialConnectionsProviders.includes(
        SocialProviders.FACEBOOK,
      );
    }
    return true;
  }

  get enableAccess() {
    return !!(
      this.socialConnectionsProviders?.length &&
      this.rootStore.userDataStore.email
    );
  }

  get shouldHideSocialAuth() {
    return this.rootStore.idpConnectionLoginStore.isIdpFirstFactorFlow;
  }

  get showIconsError() {
    const { facebookLoginStore, appleLoginStore, googleLoginStore } =
      this.rootStore;
    return (
      facebookLoginStore.hasError ||
      appleLoginStore.hasError ||
      googleLoginStore.hasError
    );
  }

  generateProviderName(key: string) {
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  setSocialProvidersAndNavigate({
    socialConnectionsProviders,
    ssoUrl,
    showAlreadyExistsNote = false,
  }: {
    socialConnectionsProviders: string[];
    ssoUrl?: string;
    showAlreadyExistsNote?: boolean;
  }) {
    ssoUrl && this.rootStore.ssoStore.setSsoPageUrl(ssoUrl);
    this.showAlreadyExistsNote = showAlreadyExistsNote;
    this.setSocialConnectionsProviders(socialConnectionsProviders);
    when(
      () => this.enableAccess,
      () => this.rootStore.navigationStore.navigate(ROUTES.SOCIAL_ONLY),
    );
  }

  shouldProceedToSocialAuthOnly(
    account: Pick<AccountData, 'userAuthenticatorsData'>,
  ) {
    const hasSocialAuth =
      !!account?.userAuthenticatorsData?.socialConnectionsProviders?.length;
    return !account?.userAuthenticatorsData?.hasPassword && hasSocialAuth;
  }
}
